//render it in contributeinput and questioninput
//render microphone and language selection popup in here
//take button code from textboxes and render in here
import React, { useEffect, useState } from 'react'
import LangSelectDropdown from '../langSelectDropdown/langSelectDropdown'
import { getProfileSettings } from '../../api'
import styles from './ButtonPanel.module.css'
import ToolTip from '../../components/Tooltip/Tooltip'
import { Send24Filled, Dismiss16Filled } from '@fluentui/react-icons'
import Button from '../ui/Button'
import MicrophonePrototype from '../Microphone/micprototype'
import type { MicrophoneHandle } from '../Microphone/micprototype'

interface ButtonPanelProps {
    updateQuestion: React.Dispatch<React.SetStateAction<string>>
    setIsTranscribing: React.Dispatch<React.SetStateAction<boolean>>
    isRecording: boolean
    setIsRecording: React.Dispatch<React.SetStateAction<boolean>>
    ref: { cancelRecording: () => void } | null
    chatType: 'ask' | 'contribute'
    sendQuestion: () => void
    sendQuestionDisabled: boolean
    handleCancel: () => void
    setSendingTranscription: React.Dispatch<React.SetStateAction<boolean>>
    micPermissionsDenied?: boolean
    appendTranscription: (text: string) => void
    /** Flag the box's contents as spoken — see MicrophonePrototype's prop doc. */
    markVoiceInput?: () => void
}

// ButtonPanel adds no imperative surface of its own — the only handle callers
// want is the recorder's — so the ref passes straight through to the mic rather
// than being re-wrapped.
const ButtonPanel = React.forwardRef<MicrophoneHandle, ButtonPanelProps>(
    (
        {
            updateQuestion,
            setIsTranscribing,
            isRecording,
            setIsRecording,
            chatType,
            sendQuestion,
            sendQuestionDisabled,
            handleCancel,
            setSendingTranscription,
            micPermissionsDenied,
            appendTranscription,
            markVoiceInput,
        },
        ref
    ) => {
        //fetch profile settings data for languagepreference object
        const [micLanguageLocale, setMicLanguageLocale] = useState('en-GB')
        const [micLanguageDisplayName, setMicLanguageDisplayName] =
            useState('en-GB')
        const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)
        useEffect(() => {
            const fetchLanguageSetting = async () => {
                try {
                    const profileSettings = await getProfileSettings(undefined)
                    const languageSettings =
                        profileSettings.languagePreference ?? null
                    const languageLocale = languageSettings?.locale ?? 'en-GB'
                    const languageName =
                        languageSettings?.language ?? 'English (United Kingdom)'
                    setMicLanguageDisplayName(languageName)
                    setMicLanguageLocale(languageLocale)
                } catch (error) {
                    console.error('Error fetching profile settings', error)
                }
            }
            fetchLanguageSetting()
        }, [])

        return (
            <div className={styles.buttonPanelContainer}>
                {isRecording ? (
                    <button
                        className={styles.cancelButton}
                        onClick={handleCancel}
                    >
                        {
                            <Dismiss16Filled primaryFill="var(--color-text-brand)" />
                        }
                    </button>
                ) : (
                    <ToolTip content="Send" side="top">
                        <Button
                            variant="icon-primary"
                            disabled={sendQuestionDisabled}
                            onClick={sendQuestion}
                        >
                            <span
                                style={{
                                    padding: 0,
                                    margin: 0,
                                    overflow: 'hidden',
                                    display: 'inline-flex',
                                }}
                            >
                                <Send24Filled
                                    primaryFill="var(--color-text-inverse)"
                                    style={{
                                        display: 'block',
                                        lineHeight: 1,
                                    }}
                                />
                            </span>
                        </Button>
                    </ToolTip>
                )}
                <MicrophonePrototype
                    ref={ref}
                    micLanguageLocale={micLanguageLocale}
                    updateQuestion={updateQuestion}
                    setIsTranscribing={setIsTranscribing}
                    isRecording={isRecording}
                    setIsRecording={setIsRecording}
                    chatType={chatType}
                    setSendingTranscription={setSendingTranscription}
                    micPermissionsDenied={micPermissionsDenied}
                    appendTranscription={appendTranscription}
                    markVoiceInput={markVoiceInput}
                />
                <LangSelectDropdown
                    micLanguageLocale={micLanguageLocale}
                    setMicLanguageLocale={setMicLanguageLocale}
                    micLanguageDisplayName={micLanguageDisplayName}
                    setMicLanguageDisplayName={setMicLanguageDisplayName}
                    isLanguageModalOpen={isLanguageModalOpen}
                    setIsLanguageModalOpen={setIsLanguageModalOpen}
                    isRecording={isRecording}
                    micPermissionsDenied={micPermissionsDenied}
                />
            </div>
        )
    }
)

export default ButtonPanel
